import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import {
  type OrderPaymentConfirmer,
  type OrderReader,
  type UserOrder,
} from '../../../domain/order/order';

@customElement('rftw-order-popup')
export class RftwOrderPopup extends LitElement {
  @property({ type: Boolean, reflect: true }) open = false;
  @property({ attribute: false }) orderReader?: OrderReader;
  @property({ attribute: false }) orderPaymentConfirmer?: OrderPaymentConfirmer;
  @state() private orders: UserOrder[] = [];
  @state() private searchedEmail = '';
  @state() private loading = false;
  @state() private error = '';
  @state() private hasSearched = false;
  @state() private confirmedOrderIds = new Set<string>();
  @state() private receiptErrors = new Map<string, string>();
  @state() private activeReceiptHelpId = '';

  static styles = css`
    :host {
      position: fixed;
      inset: 0;
      z-index: 1100;
      display: none;
      font-family: var(--font-family, 'Open Sans', sans-serif);
    }

    :host([open]) {
      display: grid;
      place-items: center;
      padding: 1.25rem;
      box-sizing: border-box;
    }

    .backdrop {
      position: absolute;
      inset: 0;
      background: rgba(27, 46, 35, 0.72);
    }

    .dialog {
      position: relative;
      display: flex;
      flex-direction: column;
      width: min(100%, 1160px);
      max-height: min(720px, calc(100vh - 2.5rem));
      overflow: hidden;
      background: #ffffff;
      border-radius: 10px;
      box-shadow: 0 18px 48px rgba(0, 0, 0, 0.28);
    }

    .dialog-header {
      display: flex;
      justify-content: space-between;
      gap: 1rem;
      padding: 2.25rem 2.25rem 1.5rem;
    }

    .eyebrow {
      margin: 0 0 0.75rem;
      color: var(--wa-color-brand, #ff4f0b);
      font-size: 1.05rem;
      font-weight: 700;
      letter-spacing: 0.2em;
      line-height: 1.2;
    }

    h2 {
      margin: 0;
      color: #1b2e23;
      font-size: 3.2rem;
      font-weight: 800;
      line-height: 1.2;
    }

    .close-button {
      display: grid;
      place-items: center;
      width: 2.25rem;
      height: 2.25rem;
      flex: 0 0 auto;
      border: 0;
      border-radius: 6px;
      background: transparent;
      color: #1b2e23;
      cursor: pointer;
      font-size: 2.4rem;
      line-height: 1;
    }

    .panel {
      display: grid;
      gap: 1.5rem;
      min-height: 0;
      overflow-y: auto;
      padding: 0 2.25rem 2.25rem;
      background: #ffffff;
    }

    .intro {
      max-width: 1000px;
      margin: 0;
      color: #4a5568;
      font-size: 1.4rem;
      line-height: 1.45;
    }

    form,
    .field {
      display: grid;
      gap: 0.4rem;
    }

    form {
      grid-template-columns: minmax(0, 1fr) 320px;
      align-items: end;
      gap: 1.75rem;
      padding-bottom: 1.5rem;
      border-bottom: 2px solid #e4e6e2;
    }

    label {
      color: #1b2e23;
      font-size: 1.1rem;
      font-weight: 700;
    }

    input {
      width: 100%;
      min-height: 2.8rem;
      box-sizing: border-box;
      padding: 0.9rem 1rem;
      border: 1px solid #bfc5bf;
      border-radius: 6px;
      background: #ffffff;
      color: #1b2e23;
      font: inherit;
      font-size: 1.1rem;
    }

    button[type='submit'] {
      min-height: 4rem;
      border: 0;
      border-radius: 6px;
      background: var(--wa-color-orange-50, #d95328);
      color: #ffffff;
      cursor: pointer;
      font: inherit;
      font-size: 1.15rem;
      font-weight: 700;
    }

    button:focus-visible,
    input:focus-visible {
      outline: 3px solid rgba(217, 83, 40, 0.35);
      outline-offset: 2px;
    }

    .message {
      margin: 0;
      padding: 0.9rem 1rem;
      border-left: 4px solid #d8d6cf;
      background: #ffffff;
      color: #4a5568;
      line-height: 1.5;
    }

    .empty-state {
      display: grid;
      place-items: center;
      min-height: 180px;
      color: #1b1d1b;
      font-size: 1.35rem;
      text-align: center;
    }

    .error {
      border-left-color: #b94220;
      background: #fff0eb;
      color: #8e2f18;
    }

    .orders {
      display: grid;
      gap: 1.25rem;
      margin: 0;
      padding: 0;
      list-style: none;
    }

    .order-group {
      display: grid;
      gap: 0.75rem;
      padding: 1.5rem 1.75rem;
      border: 1px solid #b8a362;
      border-radius: 8px;
      background: #f8f6f0;
    }

    .order-group-title {
      margin: 0 0 0.25rem;
      color: #111311;
      font-size: 1.45rem;
      font-weight: 800;
    }

    .order-row {
      display: grid;
      gap: 0.45rem;
      padding: 0.7rem 0;
      border-bottom: 2px solid #dedfd9;
    }

    .order-row:last-child {
      border-bottom: 0;
      padding-bottom: 0;
    }

    .order-row-head,
    .order-row-detail {
      display: flex;
      justify-content: space-between;
      gap: 1rem;
    }

    .order-row-head {
      color: #111311;
      font-size: 1.05rem;
      font-weight: 700;
    }

    .order-row-head strong,
    .order-row-detail strong {
      color: #111311;
      text-align: right;
    }

    .status-badge {
      display: inline-flex;
      align-items: center;
      min-height: 1.65rem;
      padding: 0.2rem 0.6rem;
      border-radius: 999px;
      font-size: 0.74rem;
      font-weight: 700;
      line-height: 1;
      white-space: nowrap;
    }

    .status-pending {
      background: #fff3d6;
      color: #8a5a00;
    }

    .status-payment-confirmed {
      background: #e5efe4;
      color: #28643e;
    }

    .status-completed {
      background: #dcecf5;
      color: #245b78;
    }

    .status-cancelled {
      background: #fff0eb;
      color: #8e2f18;
    }

    .order-row-detail {
      color: #4a5568;
      font-size: 0.9rem;
    }

    .receipt-confirmation {
      display: grid;
      grid-template-columns: minmax(0, 1fr) 184px;
      align-items: end;
      gap: 0.65rem;
      margin-top: 0.3rem;
      padding-top: 0.9rem;
      border-top: 1px solid #dedfd9;
    }

    .receipt-field {
      position: relative;
      display: grid;
      gap: 0.35rem;
    }

    .receipt-label {
      display: flex;
      align-items: center;
      gap: 0.35rem;
      color: #4a5568;
      font-size: 0.9rem;
    }

    .receipt-confirmation input {
      min-height: 3rem;
      padding: 0.65rem 0.8rem;
      font-size: 0.95rem;
    }

    .receipt-confirmation button {
      min-height: 3rem;
      border: 0;
      border-radius: 6px;
      background: var(--wa-color-orange-50, #d95328);
      color: #ffffff;
      cursor: pointer;
      font: inherit;
      font-weight: 700;
    }

    .receipt-confirmation button:disabled {
      cursor: wait;
      opacity: 0.7;
    }

    .receipt-confirmation .receipt-help {
      position: relative;
      display: inline-grid;
      place-items: center;
      width: 1rem;
      height: 1rem;
      min-height: 1rem;
      padding: 0;
      border: 1px solid #111311 !important;
      border-radius: 50%;
      background: transparent !important;
      color: #111311 !important;
      cursor: help;
      font: inherit;
      font-size: 0.68rem;
      line-height: 1;
    }

    .receipt-tooltip {
      position: absolute;
      z-index: 2;
      bottom: calc(100% + 0.6rem);
      left: 50%;
      width: 270px;
      padding: 0.75rem 0.85rem;
      border-radius: 5px;
      background: #20211e;
      color: #ffffff;
      font-size: 0.75rem;
      font-weight: 400;
      line-height: 1.45;
      opacity: 0;
      pointer-events: none;
      text-align: left;
      transform: translateX(-50%) translateY(0.25rem);
      transition:
        opacity 150ms ease,
        transform 150ms ease;
    }

    .mobile-receipt-tooltip {
      display: none;
    }

    .receipt-tooltip::after {
      position: absolute;
      top: 100%;
      left: 50%;
      width: 0;
      height: 0;
      border: 0.35rem solid transparent;
      border-top-color: #20211e;
      content: '';
      transform: translateX(-50%);
    }

    .receipt-help:hover .receipt-tooltip,
    .receipt-help:focus-visible .receipt-tooltip {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }

    .receipt-error {
      grid-column: 1 / -1;
      margin: 0;
      color: #8e2f18;
      font-size: 0.85rem;
    }

    .receipt-error::before {
      margin-right: 0.45rem;
      color: #d62434;
      content: '▲';
      font-size: 0.8rem;
    }

    .payment-confirmed-message {
      display: flex;
      align-items: center;
      gap: 0.7rem;
      margin: 0.55rem 0 0;
      padding: 0.75rem 0.9rem;
      border-radius: 6px;
      background: #e8f1e7;
      color: #385345;
      font-size: 0.9rem;
      line-height: 1.4;
    }

    .payment-confirmed-icon {
      color: #28643e;
      font-size: 1.1rem;
      font-weight: 800;
    }

    @media (max-width: 480px) {
      :host([open]) {
        padding: 0.75rem;
      }

      .dialog-header,
      .panel {
        padding: 1.25rem;
      }

      h2 {
        font-size: 2.1rem;
      }

      .eyebrow {
        font-size: 0.8rem;
      }

      .intro {
        font-size: 1rem;
      }

      form {
        grid-template-columns: 1fr;
        gap: 1rem;
      }

      button[type='submit'] {
        min-height: 3.25rem;
      }

      .order-group {
        padding: 1.1rem 1rem;
      }

      .order-group-title {
        font-size: 1.2rem;
      }

      .receipt-confirmation {
        grid-template-columns: 1fr;
      }

      .receipt-confirmation button {
        min-height: 3.25rem;
      }

      .receipt-tooltip {
        display: none;
      }

      .receipt-label {
        flex-wrap: wrap;
      }

      .mobile-receipt-tooltip {
        display: block;
        position: static;
        flex-basis: 100%;
        width: 100%;
        box-sizing: border-box;
        padding: 0.75rem 0.85rem;
        border-radius: 5px;
        background-color: #20211e !important;
        color: #ffffff !important;
        font-size: 0.75rem;
        font-weight: 400;
        line-height: 1.45;
      }
    }
  `;

  private _close() {
    this.open = false;
    this.orders = [];
    this.searchedEmail = '';
    this.error = '';
    this.hasSearched = false;
    this.confirmedOrderIds = new Set();
    this.receiptErrors = new Map();
    this.activeReceiptHelpId = '';
    this.dispatchEvent(new CustomEvent('order-popup-close'));
  }

  private async _search(event: SubmitEvent) {
    event.preventDefault();
    const form = new FormData(event.currentTarget as HTMLFormElement);
    const email = String(form.get('email') ?? '').trim();

    this.error = '';
    this.hasSearched = true;
    this.searchedEmail = email;
    this.confirmedOrderIds = new Set();
    this.receiptErrors = new Map();
    this.activeReceiptHelpId = '';

    if (!this.orderReader) {
      this.error =
        'El servicio de órdenes no está disponible. Intenta nuevamente.';
      return;
    }

    this.loading = true;
    try {
      this.orders = await this.orderReader.getOrdersByUserEmail(email);
    } catch (error) {
      this.orders = [];
      this.error =
        error instanceof Error
          ? error.message
          : 'No pudimos consultar tus órdenes. Intenta nuevamente.';
    } finally {
      this.loading = false;
    }
  }

  private _orderType(type: number) {
    const types = [
      'Inscripción Adventure Ride',
      'Camiseta',
      'Jersey',
      'Donación',
    ];

    return types[type] ?? 'Tipo desconocido';
  }

  private _orderStatus(status: number) {
    const statuses = [
      { label: 'Pendiente de pago', className: 'status-pending' },
      { label: 'Pago confirmado', className: 'status-payment-confirmed' },
      { label: 'Completada', className: 'status-completed' },
      { label: 'Cancelada', className: 'status-cancelled' },
    ];

    return (
      statuses[status] ?? {
        label: 'Estado desconocido',
        className: 'status-pending',
      }
    );
  }

  private async _confirmPayment(event: SubmitEvent, order: UserOrder) {
    event.preventDefault();
    const form = new FormData(event.currentTarget as HTMLFormElement);
    const receiptNumber = String(form.get('receiptNumber') ?? '').trim();
    const receiptErrors = new Map(this.receiptErrors);
    receiptErrors.delete(order.id);
    this.receiptErrors = receiptErrors;

    if (!this.orderPaymentConfirmer) {
      this._setReceiptError(
        order.id,
        'El servicio de pagos no está disponible. Intenta nuevamente.'
      );
      return;
    }

    try {
      const wasRegistered =
        await this.orderPaymentConfirmer.updateOrderConfirmationId(
          order.id,
          receiptNumber
        );
      if (!wasRegistered) {
        this._setReceiptError(
          order.id,
          'No encontramos ese número de recibo. Revisa tu comprobante de pago e inténtalo de nuevo.'
        );
        return;
      }

      this.confirmedOrderIds = new Set([...this.confirmedOrderIds, order.id]);
      if (this.orderReader) {
        this.orders = await this.orderReader.getOrdersByUserEmail(
          this.searchedEmail
        );
      }
    } catch (error) {
      this._setReceiptError(
        order.id,
        error instanceof Error
          ? error.message
          : 'No pudimos confirmar el pago. Intenta nuevamente.'
      );
    }
  }

  private _setReceiptError(orderId: string, message: string) {
    const receiptErrors = new Map(this.receiptErrors);
    receiptErrors.set(orderId, message);
    this.receiptErrors = receiptErrors;
  }

  private _toggleReceiptHelp(event: MouseEvent, orderId: string) {
    event.preventDefault();
    event.stopPropagation();
    this.activeReceiptHelpId =
      this.activeReceiptHelpId === orderId ? '' : orderId;
  }

  private _formatDate(value: string) {
    if (!value) return 'Sin fecha';
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? value
      : new Intl.DateTimeFormat('es-CR', { dateStyle: 'medium' }).format(date);
  }

  private _renderResults() {
    if (this.loading) {
      return html`<p class="message" aria-live="polite">
        Consultando tus órdenes...
      </p>`;
    }

    if (this.error) {
      return html`<p class="message error" role="alert">${this.error}</p>`;
    }

    if (this.hasSearched && this.orders.length === 0) {
      return html`<p class="message" aria-live="polite">
        No encontramos órdenes asociadas a ${this.searchedEmail}.
      </p>`;
    }

    if (!this.hasSearched) {
      return html`<div class="empty-state" aria-live="polite">
        Ingresa tu correo para ver tus órdenes
      </div>`;
    }

    return html`
      <ul class="orders" aria-label="Tus órdenes">
        ${this.orders.map((order) => {
          const type = this._orderType(order.type);
          const status = this._orderStatus(order.status);
          const confirmationRegistered = this.confirmedOrderIds.has(order.id);

          return html`
            <li class="order-group" aria-label="Orden de ${type}">
              <h3 class="order-group-title">${type}</h3>
              <div
                class="order-row"
                aria-label="Orden ${order.id}: ${status.label}"
              >
                <div class="order-row-head">
                  <span>Orden</span>
                  <strong>${order.id}</strong>
                </div>
                <div class="order-row-detail">
                  <span>Estado</span>
                  <span class="status-badge ${status.className}">
                    ${status.label}
                  </span>
                </div>
                <div class="order-row-detail">
                  <span>Talla</span>
                  <strong>${order.size || 'No indicada'}</strong>
                </div>
                <div class="order-row-detail">
                  <span>Nombre y número</span>
                  <strong>
                    ${order.jerseyName || 'No indicado'} ·
                    ${order.jerseyNumber || 'No indicado'}
                  </strong>
                </div>
                <div class="order-row-detail">
                  <span>Monto</span>
                  <strong>$${order.donationAmount || '0.00'}</strong>
                </div>
                <div class="order-row-detail">
                  <span>Creada</span>
                  <strong>${this._formatDate(order.createdAt)}</strong>
                </div>
                ${order.status === 0 && !confirmationRegistered
                  ? html`
                      <form
                        class="receipt-confirmation"
                        @submit=${(event: SubmitEvent) =>
                          this._confirmPayment(event, order)}
                      >
                        <div class="receipt-field">
                          <label
                            class="receipt-label"
                            for="receipt-${order.id}"
                          >
                            ¿Ya pagaste? Ingresa tu número de recibo
                            <button
                              class="receipt-help"
                              type="button"
                              aria-label="Dónde encontrar el número de recibo"
                              aria-expanded=${this.activeReceiptHelpId ===
                              order.id}
                              title="Busca el correo de MiPOS BAC Credomatic - ELEPHANT MOTO. Ahí encontrarás el número de recibo."
                              @click=${(event: MouseEvent) =>
                                this._toggleReceiptHelp(event, order.id)}
                            >
                              i
                              <span class="receipt-tooltip">
                                <strong>Dónde encontrar tu # de recibo:</strong
                                ><br />
                                1. Ve a tu correo electrónico.<br />
                                2. Busca el correo de parte de MiPOS BAC
                                Credomatic - ELEPHANT MOTO.<br />
                                3. Dentro de ese correo encontrarás un número de
                                recibo; agrégalo aquí abajo.
                              </span>
                            </button>
                            ${this.activeReceiptHelpId === order.id
                              ? html`<span class="mobile-receipt-tooltip">
                                  <strong
                                    >Dónde encontrar tu # de recibo:</strong
                                  ><br />
                                  1. Ve a tu correo electrónico.<br />
                                  2. Busca el correo de parte de MiPOS BAC
                                  Credomatic - ELEPHANT MOTO.<br />
                                  3. Dentro de ese correo encontrarás un número
                                  de recibo; agrégalo aquí abajo.
                                </span>`
                              : nothing}
                          </label>
                          <input
                            id="receipt-${order.id}"
                            name="receiptNumber"
                            placeholder="Ej: 29188300"
                            autocomplete="off"
                            required
                          />
                        </div>
                        <button type="submit">Confirmar</button>
                        ${this.receiptErrors.get(order.id)
                          ? html`<p class="receipt-error" role="alert">
                              ${this.receiptErrors.get(order.id)}
                            </p>`
                          : nothing}
                      </form>
                    `
                  : confirmationRegistered
                    ? html`
                        <p class="payment-confirmed-message">
                          <span
                            class="payment-confirmed-icon"
                            aria-hidden="true"
                            >✓</span
                          >
                          ${order.paymentConfirmationId
                            ? html`Recibo
                                <strong>${order.paymentConfirmationId}</strong>
                                recibido. Hemos confirmado su pago.`
                            : 'Hemos confirmado su pago.'}
                        </p>
                      `
                    : nothing}
              </div>
            </li>
          `;
        })}
      </ul>
    `;
  }

  render() {
    if (!this.open) return nothing;

    return html`
      <div class="backdrop" @click=${this._close}></div>
      <section
        class="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="orders-title"
      >
        <header class="dialog-header">
          <div>
            <p class="eyebrow">RIDE FOR THE WILD</p>
            <h2 id="orders-title">Ver orden</h2>
          </div>
          <button
            class="close-button"
            type="button"
            aria-label="Cerrar"
            @click=${this._close}
          >
            &times;
          </button>
        </header>
        <div class="panel">
          <p class="intro">
            Escribe el correo que usaste al registrarte o comprar una prenda
            para consultar el estado de tus órdenes.
          </p>
          <form @submit=${this._search}>
            <div class="field">
              <label for="order-email">Correo electrónico</label>
              <input
                id="order-email"
                name="email"
                type="email"
                autocomplete="email"
                required
              />
            </div>
            <button type="submit">Consultar órdenes</button>
          </form>
          ${this._renderResults()}
        </div>
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rftw-order-popup': RftwOrderPopup;
  }
}

